import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Plus, Search, Send, SlidersHorizontal, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useCatalogs, catalogMap, invalidateContacts } from "@/lib/lookups";
import { CONTACT_FIELDS } from "@/lib/conecta";
import { FieldsForm } from "@/components/finance/FieldsForm";
import { PrivateBadge } from "@/components/conecta/Bits";
import { AskEntry } from "@/components/conecta/AskEntry";
import { RedSearchButton } from "@/components/red/RedSearchButton";
import { InviteFlow } from "@/components/red/InviteFlow";
import { refreshRed, useRedEstado } from "@/lib/red";

export const useDebounced = (value, ms = 300) => {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
};

const FilterSelect = ({ value, onChange, options, placeholder, testid }) => (
  <select data-testid={testid} className="c-input !py-2 text-sm" value={value} onChange={(e) => onChange(e.target.value)}>
    <option value="">{placeholder}</option>
    {Object.entries(options).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
  </select>
);

const ContactCard = ({ c, rel, onClick, selected }) => (
  <button onClick={onClick} data-testid="contact-card-item" className={`w-full text-left c-card p-4 space-y-1.5 active:scale-[0.99] transition-transform ${selected ? "ring-2 ring-[#bc9538]" : ""}`}>
    <div className="flex items-start justify-between gap-2">
      <p className="font-semibold" data-testid="contact-card-name">{selected !== undefined && <span data-testid="contact-card-select" className={`inline-block w-4 h-4 mr-2 align-[-2px] rounded border ${selected ? "bg-[#bc9538] border-[#bc9538]" : "c-line"}`} />}{c.name}{c.nickname ? <span className="c-text-2 font-normal"> "{c.nickname}"</span> : null}</p>
      <div className="flex items-center gap-1 shrink-0">
        {c.compartible === false && <span data-testid="contact-card-not-shared" className="text-[10px] font-semibold px-1.5 py-0.5 rounded border c-line c-muted">No compartido</span>}
        {["sin_clasificar", "error"].includes(c.clasificacion_estado) && <span data-testid="contact-card-unclassified" className="text-[10px] font-semibold px-1.5 py-0.5 rounded border border-[#bc9538]/50 c-accent">Sin clasificar</span>}
        <PrivateBadge show={c.is_private_only_me} />
      </div>
    </div>
    <p className="text-xs c-text-2">{[c.company, c.position, rel[c.relationship_type], c.city].filter(Boolean).join(" · ") || "Sin datos"}</p>
    {(c.specialties.length > 0 || c.reach.length > 0) && (
      <div className="flex flex-wrap gap-1">
        {[...c.specialties, ...c.reach].slice(0, 4).map((t) => <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-[var(--c-elev)]">{t}</span>)}
      </div>
    )}
    <p className="text-[11px] c-muted">{c.opportunities_count} negocio(s)</p>
  </button>
);

const VIEWS = { "": "Todos", sin_clasificar: "Sin clasificar", no_compartidos: "No compartidos" };
const VIEW_PARAMS = { sin_clasificar: { clasificacion: "sin_clasificar" }, no_compartidos: { compartible: false } };

const ViewChips = ({ view, setView, estado }) => (
  <div className="flex gap-1.5 overflow-x-auto" data-testid="contacts-view-chips">
    {Object.entries(VIEWS).map(([k, label]) => {
      const n = k === "sin_clasificar" ? estado?.sin_clasificar : k === "no_compartidos" ? estado?.no_compartidos : null;
      return (
        <button key={k} data-testid={`contacts-view-${k || "todos"}`} onClick={() => setView(k)}
          className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${view === k ? "bg-[#0b2a47] text-white border-[#0b2a47] dark:bg-[#bc9538] dark:text-[#0b2a47] dark:border-[#bc9538]" : "c-card c-line"}`}>
          {label}{n != null ? ` (${n})` : ""}
        </button>
      );
    })}
  </div>
);

export default function RedPage() {
  const nav = useNavigate();
  const cats = useCatalogs();
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ relationship_type: "", specialty: "", sector: "" });
  const [sp] = useSearchParams();
  const [view, setView] = useState(sp.get("vista") || "");
  const [estado] = useRedEstado();
  const [showFilters, setShowFilters] = useState(false);
  const [data, setData] = useState(null);
  const [creating, setCreating] = useState(false);
  const [picked, setPicked] = useState(null);
  const [inviting, setInviting] = useState(false);
  const togglePick = (c) => setPicked((p) => { const n = { ...p }; if (n[c.id]) delete n[c.id]; else n[c.id] = c; return n; });
  const dq = useDebounced(q);

  const load = useCallback(() => {
    const params = { ...Object.fromEntries(Object.entries({ q: dq, ...filters }).filter(([, v]) => v)), ...(VIEW_PARAMS[view] || {}) };
    api.get("/contacts", { params }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [dq, filters, view]);
  useEffect(() => { load(); }, [load]);

  const create = async (payload) => {
    try {
      const { data: c } = await api.post("/contacts", payload);
      invalidateContacts();
      refreshRed();
      setCreating(false);
      toast.success("Contacto creado");
      nav(`/conecta/red/${c.id}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const lab = (kind) => Object.fromEntries((cats[kind] || []).map((i) => [i.label, i.label]));
  const setF = (k) => (v) => setFilters((s) => ({ ...s, [k]: v }));

  return (
    <div data-testid="red-page" className="space-y-3">
      <AskEntry testid="red-ask-entry" />
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 c-muted" />
          <input data-testid="contacts-search-input" className="c-input !pl-9" placeholder="Nombre, empresa, especialidad, ciudad…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <button data-testid="contacts-filters-toggle" onClick={() => setShowFilters((s) => !s)} aria-label="Filtros" className="w-12 rounded-xl c-card flex items-center justify-center"><SlidersHorizontal className="w-4 h-4" /></button>
      </div>
      {showFilters && (
        <div className="grid grid-cols-1 gap-2" data-testid="contacts-filters">
          <FilterSelect testid="filter-relationship" value={filters.relationship_type} onChange={setF("relationship_type")} options={catalogMap(cats, "relationship_types")} placeholder="Tipo de relación: todos" />
          <FilterSelect testid="filter-specialty" value={filters.specialty} onChange={setF("specialty")} options={lab("specialties")} placeholder="Especialidad: todas" />
          <FilterSelect testid="filter-sector" value={filters.sector} onChange={setF("sector")} options={lab("sectors")} placeholder="Sector: todos" />
        </div>
      )}
      <RedSearchButton estado={estado} q={q} />
      <ViewChips view={view} setView={setView} estado={estado} />
      <div className="flex items-center justify-between gap-2">
        <p data-testid="contacts-count" className="text-sm c-text-2">{data ? `${data.count} contacto(s)` : "…"}</p>
        <div className="flex gap-1.5">
          <button data-testid="invite-multi-toggle" onClick={() => setPicked(picked ? null : {})} className="px-3 py-2 text-sm rounded-xl border c-line font-semibold flex items-center gap-1.5">
            {picked ? <><X className="w-4 h-4" /> Cancelar</> : <><Send className="w-4 h-4 c-accent" /> Invitar varios</>}
          </button>
          {!picked && <button data-testid="create-contact-btn" onClick={() => setCreating(true)} className="c-btn-primary px-3.5 py-2 text-sm flex items-center gap-1.5"><Plus className="w-4 h-4" /> Nuevo</button>}
        </div>
      </div>
      {!data ? <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin c-muted" /></div> : (
        <div className="space-y-2.5">
          {data.items.length === 0 && <p className="c-card p-6 text-sm c-text-2 text-center">Sin contactos{q ? " para esta búsqueda" : " todavía"}.</p>}
          {q.trim() && estado?.activado && (
            <Link to={`/conecta/buscar?q=${encodeURIComponent(q.trim())}`} data-testid={data.items.length === 0 ? "red-search-network-cta" : "red-none-fits-link"}
              className={data.items.length === 0 ? "c-btn-primary w-full py-3 flex items-center justify-center gap-2" : "block text-center text-sm font-semibold c-accent py-1"}>
              {data.items.length === 0 ? "Buscar en la red CONECTA" : "Ninguno me sirve: buscar en la red CONECTA"}
            </Link>
          )}
          {data.items.map((c) => <ContactCard key={c.id} c={c} rel={catalogMap(cats, "relationship_types")} selected={picked ? !!picked[c.id] : undefined}
            onClick={() => (picked ? togglePick(c) : nav(`/conecta/red/${c.id}`))} />)}
        </div>
      )}
      {picked && Object.keys(picked).length > 0 && (
        <button data-testid="invite-multi-start" onClick={() => setInviting(true)} className="fixed bottom-24 inset-x-4 max-w-md mx-auto z-40 c-btn-primary py-3.5 shadow-lg flex items-center justify-center gap-2">
          <Send className="w-4 h-4" /> Invitar a {Object.keys(picked).length} por WhatsApp
        </button>
      )}
      <InviteFlow contacts={Object.values(picked || {})} open={inviting} onOpenChange={(o) => { setInviting(o); if (!o) setPicked(null); }} />
      <FieldsForm open={creating} onOpenChange={setCreating} title="Nuevo contacto" fields={CONTACT_FIELDS} item={null} onSubmit={create} testid="create-contact-sheet" />
    </div>
  );
}
